import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Ruler, Plus, Trash2, Edit2, Save, Loader2, Upload, Image as ImageIcon,
  Video, X, ChevronDown, ChevronUp, Search, Eye, ArrowLeft, FileText,
  Download, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const WORK_TYPES = [
  'Wände streichen', 'Spachteln Q2', 'Schleifen', 'Lackieren Türen',
  'Decke streichen', 'Spachteln Q3', 'Grundieren', 'Lackieren Zargen',
  'Teilflächen', 'Risse schließen', 'Tapete entfernen', 'Lackieren Heizkörper'
];

const MATERIALS = [
  'Farbe', 'Abdeckfolie', 'Pinsel', 'Verlängerung', 'Klein-Gerüst',
  'Grundierung', 'Abdeckvlies', 'Rolle', 'Schleifpapier', 'Staubsauger',
  'Spachtelmasse', 'Klebeband', 'Röllchen', 'Leiter', 'Abfall-Säcke'
];

const SURFACES = [
  'Putz', 'Beton', 'Tapete', 'Holz', 'Risse', 'Schimmel',
  'Nikotin', 'Gipskarton', 'Sandend', 'Feuchtigkeit'
];

const SCOPES = ['Nur einzelne Wände', 'Ganzer Raum', 'Ganze Wohnung/Haus'];

const emptyWall = { raum: '', wand: '', breite: '', hoehe: '', notiz: '' };

const AdminAufmass = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, form, detail
  const [editingEntry, setEditingEntry] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [fileUrls, setFileUrls] = useState({});
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '', customer_name: '', customer_phone: '', customer_address: '', customer_email: '',
    besichtigung_date: new Date().toISOString().split('T')[0],
    objekt: '', bewohnt: false, projekt_raeume: '', umfang: '',
    walls: [{ ...emptyWall }],
    summe_m2: '', decke_m2: '', tueren_anzahl: '',
    arbeiten: [], material: [], untergrund: [],
    farbton: '', deckkraft: '',
    besonderheiten: '',
    files: []
  });

  const loadEntries = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/aufmass');
      setEntries(res.data || []);
    } catch (e) { toast.error('Fehler beim Laden'); }
    setLoading(false);
  };

  const loadCustomers = async (q = '') => {
    try {
      const res = await api.get('/customers', { params: { search: q || undefined } });
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadEntries(); loadCustomers(); }, []);

  const resetForm = () => {
    setFormData({
      customer_id: '', customer_name: '', customer_phone: '', customer_address: '', customer_email: '',
      besichtigung_date: new Date().toISOString().split('T')[0],
      objekt: '', bewohnt: false, projekt_raeume: '', umfang: '',
      walls: [{ ...emptyWall }],
      summe_m2: '', decke_m2: '', tueren_anzahl: '',
      arbeiten: [], material: [], untergrund: [],
      farbton: '', deckkraft: '',
      besonderheiten: '',
      files: []
    });
    setEditingEntry(null);
    setFileUrls({});
  };

  const selectCustomer = (c) => {
    setFormData(prev => ({
      ...prev,
      customer_id: c.id,
      customer_name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company || '',
      customer_phone: c.phone || '',
      customer_address: `${c.street || ''}, ${c.postal_code || ''} ${c.city || ''}`.trim().replace(/^,\s*/, ''),
      customer_email: c.email || ''
    }));
    setCustomerSearch('');
  };

  const addWall = () => setFormData(prev => ({ ...prev, walls: [...prev.walls, { ...emptyWall }] }));
  const removeWall = (i) => setFormData(prev => ({ ...prev, walls: prev.walls.filter((_, idx) => idx !== i) }));
  const updateWall = (i, field, val) => {
    setFormData(prev => {
      const walls = [...prev.walls];
      walls[i] = { ...walls[i], [field]: val };
      return { ...prev, walls };
    });
  };

  const toggleArray = (field, val) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingFile(true);
    const newFiles = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/admin/aufmass/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        newFiles.push({ id: res.data.id, path: res.data.path, filename: res.data.filename, content_type: res.data.content_type, size: res.data.size });
      } catch (err) {
        toast.error(`Upload fehlgeschlagen: ${file.name}`);
      }
    }
    setFormData(prev => ({ ...prev, files: [...(prev.files || []), ...newFiles] }));
    setUploadingFile(false);
    if (newFiles.length) toast.success(`${newFiles.length} Datei(en) hochgeladen`);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (fileId) => {
    setFormData(prev => ({ ...prev, files: (prev.files || []).filter(f => f.id !== fileId) }));
  };

  const loadFileUrl = async (fileId) => {
    if (fileUrls[fileId]) return;
    try {
      const res = await api.get(`/admin/aufmass/files/${fileId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setFileUrls(prev => ({ ...prev, [fileId]: url }));
    } catch (e) { /* ignore */ }
  };

  const calcArea = (w) => {
    const b = parseFloat(w.breite); const h = parseFloat(w.hoehe);
    return (b && h) ? (b * h).toFixed(2) : '';
  };

  const totalWallArea = formData.walls.reduce((sum, w) => {
    const a = parseFloat(calcArea(w));
    return sum + (isNaN(a) ? 0 : a);
  }, 0);

  const handleSubmit = async () => {
    if (!formData.customer_name) { toast.error('Bitte Kundendaten ausfüllen'); return; }
    try {
      const data = { ...formData, total_wall_area: totalWallArea };
      if (editingEntry) {
        await api.put(`/admin/aufmass/${editingEntry.id}`, data);
        toast.success('Aufmaß aktualisiert');
      } else {
        await api.post('/admin/aufmass', data);
        toast.success('Aufmaß gespeichert');
      }
      resetForm();
      setView('list');
      loadEntries();
    } catch (e) { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Dieses Aufmaß wirklich löschen?')) return;
    try { await api.delete(`/admin/aufmass/${id}`); toast.success('Gelöscht'); loadEntries(); }
    catch (e) { toast.error('Fehler'); }
  };

  const handleDownloadPdf = async (entry) => {
    setDownloadingPdf(entry.id);
    try {
      const res = await api.get(`/admin/aufmass/${entry.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Aufmass_${(entry.customer_name || 'Formular').replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (e) {
      toast.error('Fehler beim Erstellen des PDFs');
    }
    setDownloadingPdf(null);
  };

  const handleSharePdf = async (entry) => {
    setDownloadingPdf(entry.id);
    try {
      const res = await api.get(`/admin/aufmass/${entry.id}/pdf`, { responseType: 'blob' });
      const file = new File([res.data], `Aufmass_${(entry.customer_name || 'Formular').replace(/\s+/g, '_')}.pdf`, { type: 'application/pdf' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: `Aufmaß - ${entry.customer_name || ''}`, text: 'Aufmaß-Formular' });
      } else {
        // Fallback: download
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF heruntergeladen (Teilen nicht verfügbar)');
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('Fehler beim Teilen');
    }
    setDownloadingPdf(null);
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      customer_id: entry.customer_id || '', customer_name: entry.customer_name || '',
      customer_phone: entry.customer_phone || '', customer_address: entry.customer_address || '',
      customer_email: entry.customer_email || '',
      besichtigung_date: entry.besichtigung_date?.split('T')[0] || '',
      objekt: entry.objekt || '', bewohnt: entry.bewohnt || false,
      projekt_raeume: entry.projekt_raeume || '', umfang: entry.umfang || '',
      walls: entry.walls?.length ? entry.walls : [{ ...emptyWall }],
      summe_m2: entry.summe_m2 || '', decke_m2: entry.decke_m2 || '', tueren_anzahl: entry.tueren_anzahl || '',
      arbeiten: entry.arbeiten || [], material: entry.material || [], untergrund: entry.untergrund || [],
      farbton: entry.farbton || '', deckkraft: entry.deckkraft || '',
      besonderheiten: entry.besonderheiten || '',
      files: entry.files || []
    });
    setView('form');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';

  // File preview component
  const FilePreview = ({ file, removable = false }) => {
    useEffect(() => { if (file.id) loadFileUrl(file.id); }, [file.id]);
    const isVideo = (file.content_type || '').startsWith('video/');
    const url = fileUrls[file.id];

    return (
      <div className="relative group border rounded-lg overflow-hidden bg-gray-100" data-testid={`file-preview-${file.id}`}>
        {isVideo ? (
          <div className="w-full h-32 flex items-center justify-center bg-gray-800">
            {url ? <video src={url} className="max-h-32 max-w-full" controls /> : <Video className="w-8 h-8 text-gray-400" />}
          </div>
        ) : (
          <div className="w-full h-32 flex items-center justify-center">
            {url ? <img src={url} alt={file.filename} className="max-h-32 max-w-full object-contain" /> : <ImageIcon className="w-8 h-8 text-gray-400" />}
          </div>
        )}
        <div className="p-1.5 text-xs truncate text-gray-600">{file.filename}</div>
        {removable && (
          <button onClick={() => removeFile(file.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  // ============ LIST VIEW ============
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto mb-6">
          <div className="bg-gradient-to-r from-[#4a6741] to-[#6b8f5e] rounded-xl p-6 shadow-lg">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3" data-testid="aufmass-title">
                  <Ruler className="w-8 h-8" /> Aufmaß-Formulare
                </h1>
                <p className="text-white/80 mt-1">Besichtigungen dokumentieren mit Fotos und Videos</p>
              </div>
              <Button onClick={() => { resetForm(); setView('form'); }} className="bg-white text-[#4a6741] hover:bg-gray-100" data-testid="new-aufmass-btn">
                <Plus className="w-4 h-4 mr-2" /> Neues Aufmaß
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <Card>
            <CardHeader><CardTitle>Alle Aufmaße ({entries.length})</CardTitle></CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-[#4a6741]" /></div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Ruler className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>Noch keine Aufmaße vorhanden</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {entries.map((entry) => (
                    <div key={entry.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border bg-gray-50 border-gray-200 hover:border-[#4a6741]/30 transition-colors" data-testid={`aufmass-entry-${entry.id}`}>
                      <div className="flex-1 mb-3 sm:mb-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-gray-900">{entry.customer_name || 'Ohne Kunde'}</h3>
                          {entry.objekt && <span className="text-xs px-2 py-0.5 rounded bg-[#4a6741]/10 text-[#4a6741]">{entry.objekt}</span>}
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                          <span>Besichtigung: {formatDate(entry.besichtigung_date)}</span>
                          {entry.total_wall_area > 0 && <span>Fläche: {entry.total_wall_area.toFixed(2)} m²</span>}
                          {(entry.files || []).length > 0 && <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {entry.files.length} Dateien</span>}
                          {(entry.arbeiten || []).length > 0 && <span>{entry.arbeiten.length} Arbeiten</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(entry)} disabled={downloadingPdf === entry.id} title="PDF herunterladen" className="text-[#4a6741] hover:text-[#3d5636] hover:bg-green-50" data-testid={`pdf-aufmass-${entry.id}`}>
                          {downloadingPdf === entry.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleSharePdf(entry)} disabled={downloadingPdf === entry.id} title="Teilen" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-testid={`share-aufmass-${entry.id}`}>
                          <Share2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(entry)} data-testid={`edit-aufmass-${entry.id}`}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ============ FORM VIEW ============
  const filteredCustomers = customerSearch.length >= 1
    ? customers.filter(c => {
        const q = customerSearch.toLowerCase();
        const name = `${c.first_name || ''} ${c.last_name || ''} ${c.company || ''}`.toLowerCase();
        return name.includes(q) || (c.phone || '').includes(q) || (c.email || '').toLowerCase().includes(q);
      })
    : [];

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); resetForm(); }} data-testid="back-to-list">
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <h1 className="text-xl font-bold text-gray-900">{editingEntry ? 'Aufmaß bearbeiten' : 'Neues Aufmaß'}</h1>
        </div>

        {/* Section 1: Kundendaten */}
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base text-[#4a6741]">1. Kundendaten</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Label>Kunde suchen</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); if (e.target.value.length >= 1) loadCustomers(e.target.value); }}
                  placeholder="Name, Telefon oder E-Mail eingeben..." className="pl-10" data-testid="customer-search" />
              </div>
              {filteredCustomers.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredCustomers.map(c => (
                    <div key={c.id} className="p-2 hover:bg-gray-100 cursor-pointer text-sm" onClick={() => selectCustomer(c)}>
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      {c.company && <span className="text-gray-500 ml-1">({c.company})</span>}
                      {c.phone && <span className="text-gray-400 ml-2">{c.phone}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Kunde (Name/Firma) *</Label><Input value={formData.customer_name} onChange={(e) => setFormData(p => ({ ...p, customer_name: e.target.value }))} className="mt-1" data-testid="input-customer-name" /></div>
              <div><Label>Telefon</Label><Input value={formData.customer_phone} onChange={(e) => setFormData(p => ({ ...p, customer_phone: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Adresse</Label><Input value={formData.customer_address} onChange={(e) => setFormData(p => ({ ...p, customer_address: e.target.value }))} className="mt-1" /></div>
              <div><Label>E-Mail</Label><Input value={formData.customer_email} onChange={(e) => setFormData(p => ({ ...p, customer_email: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>Besichtigung am</Label><Input type="date" value={formData.besichtigung_date} onChange={(e) => setFormData(p => ({ ...p, besichtigung_date: e.target.value }))} className="mt-1 w-48" data-testid="input-date" /></div>
          </CardContent>
        </Card>

        {/* Section 2: Projekt */}
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base text-[#4a6741]">2. Projekt</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Objekt</Label><Input value={formData.objekt} onChange={(e) => setFormData(p => ({ ...p, objekt: e.target.value }))} placeholder="z.B. Wohnung, Büro, Haus" className="mt-1" data-testid="input-objekt" /></div>
              <div className="flex items-end gap-3">
                <div className="flex items-center gap-2 pb-2">
                  <Checkbox checked={formData.bewohnt} onCheckedChange={(v) => setFormData(p => ({ ...p, bewohnt: !!v }))} id="bewohnt" />
                  <label htmlFor="bewohnt" className="text-sm">Bewohnt</label>
                </div>
              </div>
            </div>
            <div><Label>Projekt / Räume</Label><Input value={formData.projekt_raeume} onChange={(e) => setFormData(p => ({ ...p, projekt_raeume: e.target.value }))} placeholder="z.B. 3 Zimmer, Flur, Bad" className="mt-1" /></div>
            <div>
              <Label>Umfang</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {SCOPES.map(s => (
                  <Button key={s} variant={formData.umfang === s ? 'default' : 'outline'} size="sm"
                    className={formData.umfang === s ? 'bg-[#4a6741] hover:bg-[#3d5636]' : ''}
                    onClick={() => setFormData(p => ({ ...p, umfang: p.umfang === s ? '' : s }))}>{s}</Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Wandliste */}
        <Card className="mb-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base text-[#4a6741]">3. Wandliste</CardTitle>
            <Button variant="outline" size="sm" onClick={addWall} data-testid="add-wall-btn"><Plus className="w-3 h-3 mr-1" /> Wand</Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 pr-2">Raum</th>
                    <th className="pb-2 pr-2">Wand</th>
                    <th className="pb-2 pr-2 w-20">Breite m</th>
                    <th className="pb-2 pr-2 w-20">Höhe m</th>
                    <th className="pb-2 pr-2 w-20">m²</th>
                    <th className="pb-2 pr-2">Notiz</th>
                    <th className="pb-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.walls.map((w, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 pr-2"><Input value={w.raum} onChange={(e) => updateWall(i, 'raum', e.target.value)} placeholder="z.B. Wohnzimmer" className="h-8 text-sm" /></td>
                      <td className="py-1 pr-2"><Input value={w.wand} onChange={(e) => updateWall(i, 'wand', e.target.value)} placeholder="z.B. Links" className="h-8 text-sm" /></td>
                      <td className="py-1 pr-2"><Input type="number" step="0.01" value={w.breite} onChange={(e) => updateWall(i, 'breite', e.target.value)} className="h-8 text-sm" /></td>
                      <td className="py-1 pr-2"><Input type="number" step="0.01" value={w.hoehe} onChange={(e) => updateWall(i, 'hoehe', e.target.value)} className="h-8 text-sm" /></td>
                      <td className="py-1 pr-2 text-center font-medium text-gray-700">{calcArea(w) || '-'}</td>
                      <td className="py-1 pr-2"><Input value={w.notiz} onChange={(e) => updateWall(i, 'notiz', e.target.value)} className="h-8 text-sm" /></td>
                      <td className="py-1">{formData.walls.length > 1 && <button onClick={() => removeWall(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t text-sm">
              <div className="font-medium text-[#4a6741]">Wand-Summe: {totalWallArea.toFixed(2)} m²</div>
              <div className="flex items-center gap-2"><Label className="text-xs">Decke m²:</Label><Input type="number" step="0.01" value={formData.decke_m2} onChange={(e) => setFormData(p => ({ ...p, decke_m2: e.target.value }))} className="h-7 w-20 text-sm" /></div>
              <div className="flex items-center gap-2"><Label className="text-xs">Türen Anzahl:</Label><Input type="number" value={formData.tueren_anzahl} onChange={(e) => setFormData(p => ({ ...p, tueren_anzahl: e.target.value }))} className="h-7 w-16 text-sm" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Arbeiten */}
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base text-[#4a6741]">4. Arbeiten</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {WORK_TYPES.map(w => (
                <label key={w} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${formData.arbeiten.includes(w) ? 'bg-[#4a6741]/10 border-[#4a6741]' : 'border-gray-200 hover:border-gray-300'}`}>
                  <Checkbox checked={formData.arbeiten.includes(w)} onCheckedChange={() => toggleArray('arbeiten', w)} />
                  {w}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Material */}
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base text-[#4a6741]">5. Material und Werkzeug</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {MATERIALS.map(m => (
                <label key={m} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${formData.material.includes(m) ? 'bg-[#4a6741]/10 border-[#4a6741]' : 'border-gray-200 hover:border-gray-300'}`}>
                  <Checkbox checked={formData.material.includes(m)} onCheckedChange={() => toggleArray('material', m)} />
                  {m}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Section 6: Untergrund */}
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base text-[#4a6741]">6. Untergrund und Zustand</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {SURFACES.map(s => (
                <label key={s} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${formData.untergrund.includes(s) ? 'bg-[#4a6741]/10 border-[#4a6741]' : 'border-gray-200 hover:border-gray-300'}`}>
                  <Checkbox checked={formData.untergrund.includes(s)} onCheckedChange={() => toggleArray('untergrund', s)} />
                  {s}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Section 7: Farbton */}
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base text-[#4a6741]">7. Farbton und Ausführung</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Farbton / RAL / Marke</Label><Input value={formData.farbton} onChange={(e) => setFormData(p => ({ ...p, farbton: e.target.value }))} placeholder="z.B. RAL 9010, Alpinaweiß" className="mt-1" /></div>
              <div><Label>Deckkraft / Nassabrieb</Label><Input value={formData.deckkraft} onChange={(e) => setFormData(p => ({ ...p, deckkraft: e.target.value }))} placeholder="optional" className="mt-1" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Section 8: Besonderheiten */}
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base text-[#4a6741]">8. Besonderheiten und Notizen</CardTitle></CardHeader>
          <CardContent>
            <Textarea value={formData.besonderheiten} onChange={(e) => setFormData(p => ({ ...p, besonderheiten: e.target.value }))}
              placeholder="Besondere Hinweise, Zugangsdaten, Parkplatz, etc." rows={4} data-testid="input-notes" />
          </CardContent>
        </Card>

        {/* Section 9: Fotos & Videos */}
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-base text-[#4a6741]">9. Fotos und Videos</CardTitle></CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#4a6741]/50 transition-colors">
              <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple onChange={handleFileUpload} className="hidden" data-testid="file-input" />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} data-testid="upload-btn">
                {uploadingFile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploadingFile ? 'Wird hochgeladen...' : 'Fotos / Videos hochladen'}
              </Button>
              <p className="text-xs text-gray-400 mt-2">JPG, PNG, WEBP, MP4, MOV</p>
            </div>
            {(formData.files || []).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
                {formData.files.map(f => <FilePreview key={f.id} file={f} removable={true} />)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex gap-3 mb-8">
          <Button variant="outline" onClick={() => { setView('list'); resetForm(); }} className="flex-1">Abbrechen</Button>
          {editingEntry && (
            <>
              <Button variant="outline" onClick={() => handleDownloadPdf(editingEntry)} disabled={downloadingPdf === editingEntry.id} className="text-[#4a6741] border-[#4a6741]/30" data-testid="pdf-btn">
                {downloadingPdf === editingEntry.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />} PDF
              </Button>
              <Button variant="outline" onClick={() => handleSharePdf(editingEntry)} disabled={downloadingPdf === editingEntry.id} className="text-blue-600 border-blue-300" data-testid="share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Teilen
              </Button>
            </>
          )}
          <Button onClick={handleSubmit} className="flex-1 bg-[#4a6741] hover:bg-[#3d5636] text-white" data-testid="save-aufmass-btn">
            <Save className="w-4 h-4 mr-2" /> {editingEntry ? 'Aktualisieren' : 'Aufmaß speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAufmass;
